import { useStore } from "@roc/client-portal-shared/hooks";
import { bbox, center, circle, featureCollection } from "@turf/turf";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MapComponent, { Layer, LayerProps, Marker, NavigationControl, Source } from "react-map-gl";
import RoomIcon from '@material-ui/icons/Room';
import { InspectionData, InspectionDetails, InspectionPhoto } from "../types/types";
import { Box, Button, ButtonGroup, makeStyles, Typography } from "@material-ui/core";

const SP_MARKER_COLOR = "#FB8C00";
const PHOTO_MARKER_COLOR = "#007cbf";
const ALLOWED_RADIUS_COLOR = "#E6D700";
const PHOTO_CIRCLE_COLOR = "#007cbf";
const PHOTO_CIRCLE_OPACITY = 0.1;
const SP_CIRCLE_OPACITY = 0.3;

const MAP_STYLES = [
  { label: "Map", value: "mapbox://styles/mapbox/streets-v11" },
  { label: "Satellite", value: "mapbox://styles/mapbox/satellite-streets-v12" },
];

interface InspectionPhotosMapProps {
  inspectionDetails: InspectionDetails;
  inspectionPhotos: InspectionPhoto[];
  displayPropertyArea?: boolean;
  displayMapLegend?: boolean;
  onInspectionPhotoClick?: (inspectionPhotoId) => void;
}


const useStyles = makeStyles(() => ({
  mapLegend: {
    position: "absolute",
    top: 10,
    left: 10,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    backgroundColor: "#FFFFFF",
    borderRadius: "10px",
    padding: "8px",
    zIndex: 2,
    boxShadow: "0 0 0 2px rgba(0, 0, 0, 0.1)"
  },
  photoCircle: {
    width: '25px',
    height: '25px',
    borderRadius: '50%',
    backgroundColor: `rgb(0, 124, 191, ${PHOTO_CIRCLE_OPACITY})`,
    border: `1px solid ${PHOTO_CIRCLE_COLOR}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  subjectPropertyCircle: {
    width: '25px',
    height: '25px',
    borderRadius: '50%',
    backgroundColor: ALLOWED_RADIUS_COLOR,
    opacity: SP_CIRCLE_OPACITY,
    border: `1px solid ${ALLOWED_RADIUS_COLOR}`
  },
  legendItemContainer: {
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    gridGap: "8px"
  },
  styleToggle: {
    position: "absolute",
    bottom: 24,
    left: 10,
    zIndex: 2,
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    boxShadow: "0 0 0 2px rgba(0, 0, 0, 0.1)"
  },
  toggleButton: {
    textTransform: "none",
    padding: "2px 12px",
    fontSize: "13px"
  },
  toggleButtonActive: {
    backgroundColor: "#007cbf !important",
    color: "#FFFFFF !important"
  }
}));

export const InspectionPhotosMap = observer(({ inspectionDetails, inspectionPhotos: allInspectionPhotos, displayMapLegend, displayPropertyArea = true, onInspectionPhotoClick }: InspectionPhotosMapProps) => {

  const inspectionPhotos = (allInspectionPhotos || []).filter(
    photo =>
      typeof photo?.locationData?.latitude === 'number' &&
      typeof photo?.locationData?.longitude === 'number'
  );

  //Fallback component when we have no data
  if (!inspectionDetails || inspectionPhotos.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
        No Information to show!
      </Box>
    );
  };

  //Map component
  return (
    <Box position="relative" width="100%" height="100%">
      {displayMapLegend && (<MapLegend allowedRadius={inspectionDetails.allowedRadiusM} displayPropertyArea={displayPropertyArea} />)}
      <Map inspectionData={inspectionDetails} inspectionPhotosData={inspectionPhotos} displayPropertyArea={displayPropertyArea} onInspectionPhotoClick={onInspectionPhotoClick} />
    </Box>
  )
});

interface MapLegendProps {
  allowedRadius: number,
  displayPropertyArea: boolean;
}


const MapLegend = ({ allowedRadius, displayPropertyArea }: MapLegendProps) => {
  const classes = useStyles();

  return (
    <Box className={classes.mapLegend}>
      <Typography variant="h6">Legend</Typography>
      {displayPropertyArea && (
        <Box className={classes.legendItemContainer}>
          <RoomIcon style={{ fontSize: 26, color: SP_MARKER_COLOR }} />
          <Typography variant="body2">Property Location</Typography>
        </Box>
      )}
      <Box className={classes.legendItemContainer}>
        <Box className={classes.subjectPropertyCircle} />
        <Typography variant="body2">{`Allowed photo capture area (${allowedRadius}m)`}</Typography>
      </Box>
      <Box className={classes.legendItemContainer}>
        <Box className={classes.photoCircle}>
          <RoomIcon style={{ fontSize: 21, color: PHOTO_MARKER_COLOR }} />
        </Box>
        <Typography variant="body2">Approximate photo location</Typography>
      </Box>
    </Box>
  );
};

interface MapProps {
  inspectionData: InspectionData;
  inspectionPhotosData: InspectionPhoto[];
  displayPropertyArea: boolean;
  onInspectionPhotoClick: (inspectionPhotoId) => void;
}

const Map = observer(({ inspectionData, inspectionPhotosData, displayPropertyArea, onInspectionPhotoClick }: MapProps) => {

  const classes = useStyles();
  const mapRef = useRef(null);
  const { environmentStore } = useStore();
  const token = environmentStore.environmentVariables.mapBoxToken;
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapStyle, setMapStyle] = useState(MAP_STYLES[0].value);

  //GeoJSON circles for map
  const photosCirclesGeoJSON = useMemo(() => {
    if (!inspectionPhotosData || inspectionPhotosData.length === 0) return null;

    const features = inspectionPhotosData.map((photo) => {
      return circle(
        [photo?.locationData?.longitude, photo?.locationData?.latitude],
        photo?.locationData?.accuracy / 1000, // convert meters to kilometers
        {
          steps: 64,
          units: "kilometers",
          properties: {
            inspectionPhotoId: photo.inspectionPhotoId,
          },
        }
      );
    });
    return featureCollection(features);
  }, [inspectionPhotosData]);

  //Subject property circle
  const subjectPropertyCirclePolygon = useMemo(() => (inspectionData ? featureCollection([circle(
    [inspectionData.longitude, inspectionData.latitude],
    inspectionData?.allowedRadiusM / 1000, // turf expects km
    {
      steps: 64,
      units: "kilometers",
    }
  )]) : null), [inspectionData, inspectionData?.longitude, inspectionData?.latitude]);

  const handleMapClick = useCallback((event) => {
    const features = event.features;

    if (features && features.length > 0) {
      const clickedFeature = features.find((f) => f.layer.id === "inspection-photo-circles");

      if (clickedFeature) {
        onInspectionPhotoClick && onInspectionPhotoClick(clickedFeature.properties.inspectionPhotoId);
      }
    }
  }, []);

  const points = useMemo(() => {
    const merged = featureCollection([
      ...(photosCirclesGeoJSON?.features ?? []),
      ...(subjectPropertyCirclePolygon?.features ?? []),
    ]);
    return merged;
  }, [photosCirclesGeoJSON, subjectPropertyCirclePolygon]);

  const centerPoint = center(points);
  const [longitude, latitude] = centerPoint.geometry.coordinates;

  useEffect(() => {
    if (mapLoaded && mapRef?.current) {
      const bounds = bbox(points);

      mapRef.current.fitBounds(
        [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
        { padding: 40, duration: 1000 }
      );
    }
  }, [mapLoaded, points]);

  return (
    <>
      <ButtonGroup className={classes.styleToggle} size="small">
        {MAP_STYLES.map((style) => (
          <Button
            key={style.value}
            className={`${classes.toggleButton} ${mapStyle === style.value ? classes.toggleButtonActive : ""}`}
            onClick={() => setMapStyle(style.value)}
          >
            {style.label}
          </Button>
        ))}
      </ButtonGroup>
      <MapComponent
        ref={mapRef}
        initialViewState={{
          longitude: longitude,
          latitude: latitude,
          zoom: 17,
        }}
        mapStyle={mapStyle}
        mapboxAccessToken={token}
        interactiveLayerIds={["inspection-photo-circles"]}
        onClick={handleMapClick}
        onLoad={() => setMapLoaded(true)}
      >
      <NavigationControl position="top-right" />
      {inspectionData && displayPropertyArea && (
        <Source id="subject-property-radius" type="geojson" data={subjectPropertyCirclePolygon}>
          <Layer {...subjectPropertyCircleLayer}
          />
        </Source>
      )}
      <Source id="inspection-photo-circles" type="geojson" data={photosCirclesGeoJSON}>
        <Layer {...circleLayer} />
      </Source>

      {inspectionPhotosData.map((photo) => (
        <Marker longitude={photo?.locationData?.longitude} latitude={photo?.locationData?.latitude} >
          <RoomIcon style={{ fontSize: 30, color: PHOTO_MARKER_COLOR }} />
        </Marker>
      ))
      }
      <Marker longitude={inspectionData.longitude} latitude={inspectionData.latitude}>
        <RoomIcon style={{ fontSize: 40, color: SP_MARKER_COLOR }} />
      </Marker>
      </MapComponent>
    </>
  );
});

const subjectPropertyCircleLayer: LayerProps = {
  id: "subject-property-radius",
  type: "fill",
  paint: {
    "fill-color": ALLOWED_RADIUS_COLOR,
    "fill-opacity": SP_CIRCLE_OPACITY,
    "fill-outline-color": ALLOWED_RADIUS_COLOR,
  },
};

const circleLayer: LayerProps = {
  id: "inspection-photo-circles",
  type: "fill",
  paint: {
    "fill-color": PHOTO_CIRCLE_COLOR,
    "fill-opacity": PHOTO_CIRCLE_OPACITY,
    "fill-outline-color": PHOTO_CIRCLE_COLOR,
  },
};